import { ApiRequestError } from '@app/lib/api'
import { type ReactNode, useEffect, useId, useRef } from 'react'
import { Link } from 'react-router'
import { ContactEmail } from './site'

export function PageSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <output className="stack">
      <span className="sr-only">Loading</span>
      <div className="skeleton" style={{ width: '40%', height: '2rem' }} aria-hidden="true" />
      {Array.from({ length: rows }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <div key={i} className="skeleton" style={{ height: '4rem' }} aria-hidden="true" />
      ))}
    </output>
  )
}

/**
 * Screen-reader announcement for results that change under the user (filters, tabs, polling).
 * Renders nothing visible; the text is read politely whenever it changes.
 */
export function LiveStatus({ children }: { children: ReactNode }) {
  return (
    <output className="sr-only" aria-live="polite">
      {children}
    </output>
  )
}

/** Adds the "(opens in a new tab)" hint for target="_blank" links. */
export function NewTab() {
  return (
    <>
      <span aria-hidden="true"> ↗</span>
      <span className="sr-only"> (opens in a new tab)</span>
    </>
  )
}

export function ErrorNotice({
  error,
  title = 'Something went wrong',
}: {
  error: unknown
  title?: string
}) {
  const message =
    error instanceof ApiRequestError
      ? error.message
      : error instanceof Error
        ? error.message
        : String(error)
  // Client errors (bad input, not found, no access) explain themselves; anything else is on us.
  const ours = !(error instanceof ApiRequestError && error.status < 500)
  return (
    <div className="alert alert--error" role="alert">
      <strong>{title}</strong>
      <div className="small">{message}</div>
      {ours ? (
        <div className="small">
          If this keeps happening, email <ContactEmail />.
        </div>
      ) : null}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Badge({
  children,
  tone = 'default',
}: {
  children: ReactNode
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info'
}) {
  return <span className={`badge${tone === 'default' ? '' : ` badge--${tone}`}`}>{children}</span>
}

const STATUS_TONES: Record<
  string,
  'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info'
> = {
  draft: 'default',
  submitted: 'info',
  under_review: 'info',
  needs_info: 'warning',
  approved: 'success',
  waitlisted: 'warning',
  declined: 'danger',
  withdrawn: 'default',
  cancelled: 'default',
  pending: 'warning',
  processing: 'info',
  succeeded: 'success',
  failed: 'danger',
  refunded: 'default',
  partially_refunded: 'warning',
  published: 'success',
  open: 'success',
  closed: 'default',
  archived: 'default',
  active: 'success',
  paused: 'warning',
  past_due: 'danger',
  needs_response: 'danger',
  warning_needs_response: 'danger',
  won: 'success',
  lost: 'danger',
  completed: 'success',
  paid: 'success',
  shipped: 'info',
  delivered: 'success',
  sold: 'success',
  reserved: 'warning',
  available: 'default',
  assigned: 'info',
  confirmed: 'success',
  checked_in: 'success',
  no_show: 'danger',
  synced: 'success',
  connected: 'success',
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONES[status] ?? 'default'}>{status.replace(/_/g, ' ')}</Badge>
}

interface FieldProps {
  label: string
  help?: ReactNode
  error?: string | null
  required?: boolean
  children: (id: string, describedBy: string | undefined) => ReactNode
}

/**
 * Label + control + help/error. The control the child renders gets `aria-describedby` and
 * `aria-invalid` wired automatically, so call sites only need to pass the id through.
 */
export function Field({ label, help, error, required, children }: FieldProps) {
  const id = useId()
  const helpId = help ? `${id}-help` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [helpId, errId].filter(Boolean).join(' ') || undefined
  const wrap = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = wrap.current?.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`)
    if (!el) return
    // Only touch ids this Field owns, so callers that describe the control themselves keep theirs.
    const own = (el.getAttribute('aria-describedby') ?? '')
      .split(/\s+/)
      .filter((t) => t && !t.startsWith(`${id}-`))
    const next = [...own, ...(describedBy ? describedBy.split(' ') : [])].join(' ')
    if (next) el.setAttribute('aria-describedby', next)
    else el.removeAttribute('aria-describedby')
    if (error) el.setAttribute('aria-invalid', 'true')
    else if (el.getAttribute('aria-invalid') === 'true') el.removeAttribute('aria-invalid')
  })
  return (
    <div className="field" ref={wrap}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? (
          <>
            <span aria-hidden="true"> *</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : null}
      </label>
      {children(id, describedBy)}
      {help ? (
        <div id={helpId} className="field__help">
          {help}
        </div>
      ) : null}
      {error ? (
        <div id={errId} className="field__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  )
}

export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])
  return (
    <dialog ref={ref} className="dialog" onClose={onClose} aria-labelledby={titleId}>
      <div className="dialog__panel">
        <div className="cluster cluster--between">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {open ? children : null}
      </div>
    </dialog>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
}: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  back?: { to: string; label: string }
}) {
  return (
    <div className="page-header">
      <div>
        {back ? (
          <Link to={back.to} className="small muted">
            ← {back.label}
          </Link>
        ) : null}
        <h1>{title}</h1>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="cluster">{actions}</div> : null}
    </div>
  )
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
}) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
      {hint ? <div className="tiny faint">{hint}</div> : null}
    </div>
  )
}

export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  const paths: Record<string, string> = {
    home: 'M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
    map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zm0 0v14m6-12v14',
    calendar:
      'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
    heart: 'M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z',
    user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 9a8 8 0 0 1 16 0',
    menu: 'M4 7h16M4 12h16M4 17h16',
    search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zm9 16-4-4',
    bag: 'M6 8h12l1 13H5zm3 0V6a3 3 0 0 1 6 0v2',
    star: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z',
    bell: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4zm4 4h4',
    check: 'm5 12 5 5L20 7',
    x: 'M6 6l12 12M18 6 6 18',
    plus: 'M12 5v14M5 12h14',
    external: 'M14 4h6v6m0-6L10 14M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5',
    download: 'M12 3v12m0 0 4-4m-4 4-4-4M4 17v3h16v-3',
    logo: 'M12 2 3 7v10l9 5 9-5V7z',
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name] ?? paths.logo} />
    </svg>
  )
}

export function formatDateTime(
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
) {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    ...opts,
  }).format(new Date(iso))
}

export function formatDate(iso: string | null | undefined) {
  return formatDateTime(iso, { dateStyle: 'medium' })
}

export function formatMoney(cents: number | null | undefined) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    (cents ?? 0) / 100,
  )
}
